/**
 * Bande de concentration — logique pure (Boîte à outils, Tle).
 *
 * Mₙ, moyenne d'un échantillon de n copies indépendantes de X, d'espérance
 * μ et de variance V. Inégalité de concentration (Bienaymé-Tchebychev
 * appliquée à Mₙ) : P(|Mₙ − μ| ≥ δ) ≤ V/(nδ²). On tire 200 échantillons
 * pour chaque n, on compte les moyennes HORS de la bande ]μ − δ ; μ + δ[
 * (une moyenne sur le bord, |Mₙ − μ| = δ, compte comme hors de la bande),
 * et l'on compare la proportion observée au majorant, qui porte sur la
 * PROBABILITÉ : il est souvent large ; il tend vers 0 quand n grandit, d'où
 * P(|Mₙ − μ| ≥ δ) → 0 (loi faible des grands nombres).
 */

import { div, frac, fromDecimal, mul, value, type Frac } from '../../ui/exact';

export const SIZES = [10, 50, 100, 500, 1000] as const;
export type Size = (typeof SIZES)[number];
export const SAMPLES = 200;

export interface Law {
  id: string;
  label: string;
  /** ce que vaut X */
  text: string;
  mean: number;
  variance: number;
  /** V(X) exacte */
  V: Frac;
  /** μ et V écrits exactement */
  meanText: string;
  varText: string;
  /** δ proposés (adaptés à l'échelle de X) */
  deltas: readonly number[];
  /** tirage de X */
  draw: (rng: () => number) => number;
  /** fenêtre des moyennes affichées */
  lo: number;
  hi: number;
}

export const LAWS: readonly Law[] = [
  {
    id: 'coin',
    label: 'Pièce (pile = 1)',
    text: 'X vaut 1 si pile, 0 sinon : loi de Bernoulli de paramètre 1/2',
    mean: 0.5,
    variance: 0.25,
    V: frac(1, 4),
    meanText: '1/2',
    varText: '1/4',
    deltas: [0.05, 0.1, 0.2],
    draw: (rng) => (rng() < 0.5 ? 1 : 0),
    lo: 0,
    hi: 1,
  },
  {
    id: 'six',
    label: 'Dé : faire 6',
    text: 'X vaut 1 si le dé donne 6, 0 sinon : Bernoulli de paramètre 1/6',
    mean: 1 / 6,
    variance: 5 / 36,
    V: frac(5, 36),
    meanText: '1/6',
    varText: '5/36',
    deltas: [0.05, 0.1, 0.2],
    draw: (rng) => (rng() < 1 / 6 ? 1 : 0),
    lo: 0,
    hi: 0.7,
  },
  {
    id: 'die',
    label: 'Valeur du dé',
    text: 'X est le résultat d’un dé équilibré (1 à 6)',
    mean: 3.5,
    variance: 35 / 12,
    V: frac(35, 12),
    meanText: '7/2',
    varText: '35/12',
    deltas: [0.2, 0.5, 1],
    draw: (rng) => 1 + Math.floor(rng() * 6),
    lo: 1,
    hi: 6,
  },
];

export const lawById = (id: string) => LAWS.find((l) => l.id === id) ?? LAWS[0]!;

/** moyennes de `count` échantillons de taille n */
export function sampleMeans(law: Law, n: number, rng: () => number, count = SAMPLES): number[] {
  return Array.from({ length: count }, () => {
    let s = 0;
    for (let i = 0; i < n; i++) s += law.draw(rng);
    return s / n;
  });
}

/** |Mₙ − μ| ≥ δ : la moyenne sort de la bande (avec une tolérance d'arrondi) */
export const outside = (law: Law, m: number, delta: number) =>
  Math.abs(m - law.mean) >= delta - 1e-12;

export const outsideCount = (law: Law, means: readonly number[], delta: number) =>
  means.filter((m) => outside(law, m, delta)).length;

/** majorant de Bienaymé-Tchebychev V/(nδ²), plafonné à 1 */
export const bound = (law: Law, n: number, delta: number) =>
  Math.min(1, law.variance / (n * delta * delta));

/** le majorant V/(nδ²) en fraction exacte (avant plafonnement) */
export function boundFrac(law: Law, n: number, delta: number): Frac {
  const d = fromDecimal(delta);
  return div(law.V, mul(frac(n), mul(d, d)));
}

/** le majorant est-il ≥ 1 ? (l'inégalité n'apprend alors rien) */
export const boundUseless = (law: Law, n: number, delta: number) =>
  value(boundFrac(law, n, delta)) >= 1;

/** « 25 % », « 2,5 % » ; `up` : arrondi au dixième SUPÉRIEUR (un majorant reste un majorant) */
export function pct(v: number, up = false): string {
  const r = (up ? Math.ceil(v * 1000 - 1e-9) : Math.round(v * 1000)) / 10;
  return `${String(r).replace('.', ',')}\u00a0%`;
}

/** plus petit n (entier) pour lequel le majorant vaut au plus `risk` */
export function nFor(law: Law, delta: number, risk: number): number {
  return Math.ceil(law.variance / (risk * delta * delta) - 1e-9);
}
